// Admin Setup Page - First Time Setup
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from '../utils/api';
import { useAdminAuth } from '../utils/authContext';
import { useAdminTranslation } from '../utils/i18nContext';
import { toast } from 'sonner';
import { Shield, Lock } from 'lucide-react';

const AdminSetup = () => {
  const navigate = useNavigate();
  const { t } = useAdminTranslation();
  const { setup } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: 'office@neuro-mesh.io',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 14) {
      toast.error(t('password_requirements'));
      return;
    }

    setLoading(true);
    try {
      await setup(formData.email, formData.password, formData.confirmPassword);
      console.log('Setup successful');
      toast.success('Admin account created successfully!');
      
      // Wait for auth state to update, then redirect
      setTimeout(() => {
        navigate('/neuro-admin-access/dashboard');
      }, 1000);
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.message || 'Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#0A0A0A] border-cyan-500/20">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="w-16 h-16 text-cyan-400" />
          </div>
          <CardTitle className="text-2xl text-white">{t('setup_admin')}</CardTitle>
          <p className="text-sm text-gray-400 mt-2">
            Create your admin account. {t('office_email_only')}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-300 block mb-2">{t('email')}</label>
              <Input
                type="email"
                value={formData.email}
                disabled
                className="bg-[#1A1A1A] border-white/10 text-gray-400"
              />
            </div>

            <div>
              <label className="text-sm text-gray-300 block mb-2">{t('password')}</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-[#1A1A1A] border-white/10 text-white"
                required
                minLength={14}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('password_requirements')}
              </p>
            </div>

            <div>
              <label className="text-sm text-gray-300 block mb-2">{t('confirm_password')}</label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="bg-[#1A1A1A] border-white/10 text-white"
                required
                minLength={14}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-semibold"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? t('loading') : t('setup')}
            </Button>

            <div className="text-center mt-4">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link to="/neuro-admin-access/login" className="text-cyan-400 hover:text-cyan-300">
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSetup;
